import React, { useContext } from 'react'
import PrintIcon from '@material-ui/icons/Print'
import DownloadIcon from '@material-ui/icons/PictureAsPdf'
import DeclineIcon from '@material-ui/icons/Cancel'
import MoreIcon from '@material-ui/icons/ExpandMore'

import { Button, PopupMenu, PopupMenuItem, useTranslation } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import type { QuoteDocument } from 'paintscout'
import { getObjectLabels } from '@paintscout/util/builder'

import { usePresentationNav } from '../PresentationNavContext'

import { QuoteContext } from '../../../context/QuoteContext'

export interface PresentationNavMoreProps {}

const WORK_ORDER_VIEW = 'work-order'

export default function PresentationNavMore({ ..._props }: PresentationNavMoreProps) {
  const { options } = useClientOptions()
  const { __ } = useTranslation()

  const navContext = usePresentationNav()
  const { onMoreClick, view } = navContext

  const { quote } = useContext(QuoteContext)

  const allowResponse = navContext.allowResponse && view !== WORK_ORDER_VIEW && !quote?.is_invoice

  const objectLabels = getObjectLabels({ options, quote })

  const allowDecline = allowResponse && options?.options?.allowDecline

  const declineLabel = getDeclineLabel({ quote, objectLabels })
  const printLabel = getActionLabel('Print')
  const downloadLabel = getActionLabel('Download')

  return (
    <PopupMenu
      component={
        <Button icon={MoreIcon} variant={'text'}>
          {__('More')}
        </Button>
      }
    >
      <PopupMenuItem
        icon={PrintIcon}
        onClick={(ev) => {
          onMoreClick(ev, 'print')
        }}
      >
        {printLabel}
      </PopupMenuItem>
      <PopupMenuItem
        icon={DownloadIcon}
        onClick={(ev) => {
          onMoreClick(ev, 'download')
        }}
      >
        {downloadLabel}
      </PopupMenuItem>
      {allowDecline && (
        <PopupMenuItem
          icon={DeclineIcon}
          onClick={(ev) => {
            onMoreClick(ev, 'decline')
          }}
        >
          {declineLabel}
        </PopupMenuItem>
      )}
    </PopupMenu>
  )

  function getActionLabel(action: 'Print' | 'Download') {
    if (view === WORK_ORDER_VIEW) {
      return `${__(action)} ${__(objectLabels.workOrder.value)}`
    } else {
      return `${__(action)} ${__(objectLabels.quote.value)}`
    }
  }

  function getDeclineLabel({
    quote,
    objectLabels
  }: {
    quote: QuoteDocument
    objectLabels: ReturnType<typeof getObjectLabels>
  }) {
    if (objectLabels.declineButton) {
      return objectLabels.declineButton.value
    } else {
      const item =
        quote?.is_invoice && quote?.totals?.pending
          ? __(objectLabels.additionalWork.value)
          : __(objectLabels.quote.value)
      return `${__('Decline')} ${item}`
    }
  }
}
